import { Check } from "lucide-react";

import type { ProductDetails } from "@/lib/product-details";

export function ProductEditorial({
  details,
  fallbackDescription,
}: {
  details: ProductDetails;
  fallbackDescription?: string;
  reviews?: any[];
  relatedProducts?: any[];
}) {
  const overview = details.overview;
  const specifications = details.specifications;

  return (
    <div className="mt-16 w-full border-t border-border/70 pt-14 sm:mt-24 sm:pt-16">
      <section aria-labelledby="overview-heading">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Overview
        </p>
        <h2
          id="overview-heading"
          className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
        >
          {overview?.heading || "Built for a dependable session"}
        </h2>
        <div className="mt-8 grid gap-10 md:grid-cols-[minmax(0,1.2fr)_minmax(16rem,0.8fr)]">
          <div className="whitespace-pre-line text-base leading-7 text-foreground/90">
            {overview?.body || fallbackDescription || "Product details are coming soon."}
          </div>
          <ul className="grid content-start gap-5">
            {(overview?.features || []).map((feature) => (
              <li key={`${feature.title}-${feature.description}`} className="flex gap-3">
                <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Check className="size-4" />
                </span>
                <div>
                  <h3 className="font-medium">{feature.title}</h3>
                  <p className="mt-0.5 text-sm leading-5 text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {specifications?.groups?.length ? (
        <section
          aria-labelledby="specifications-heading"
          className="mt-10 border-t border-border/70 pt-14 sm:mt-16 sm:pt-16"
        >
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Specifications
          </p>
          <h2
            id="specifications-heading"
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
          >
            {specifications.heading || "Requirements and compatibility"}
          </h2>
          <div className="mt-8 overflow-hidden rounded-xl border border-border">
            {specifications.groups.map((group) => (
              <div key={group.title}>
                {group.title && (
                  <p className="border-b border-border bg-muted/40 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.title}
                  </p>
                )}
                {group.items.map((item) => (
                  <div
                    key={`${group.title}-${item.label}`}
                    className="grid gap-2 border-b border-border px-5 py-4 last:border-b-0 sm:grid-cols-[minmax(10rem,0.35fr)_1fr]"
                  >
                    <span className="text-sm text-muted-foreground">{item.label}</span>
                    <span className="text-sm">{item.value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
